/**
 * CommandManager - Manages command execution and undo/redo history
 *
 * Features:
 * - Executes commands with automatic error handling
 * - Maintains history stack for undo/redo
 * - Prevents duplicate operations (deduplication)
 * - Provides undo/redo functionality
 *
 * Usage:
 * ```typescript
 * const manager = new CommandManager();
 *
 * // Execute command
 * await manager.execute(new CreateScreenCommand(...));
 *
 * // Undo last command
 * await manager.undo();
 *
 * // Redo last undone command
 * await manager.redo();
 * ```
 *
 * Sprint: sprint-2
 * Phase: 3.2 (Implementation)
 * Related: docs/command-pattern-design.md
 */

import type { Command, CommandResult } from './Command';

/**
 * Options for command execution
 */
export interface ExecuteOptions {
  /** Skip if duplicate operation is pending */
  deduplicate?: boolean;
  /** Add to undo history */
  recordHistory?: boolean;
}

/**
 * CommandManager - Manages command execution and undo/redo history
 */
export class CommandManager {
  // History stacks
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];

  // Deduplication: track pending operations by key
  private pendingOperations: Map<string, Promise<CommandResult>> = new Map();

  // Max history size (prevent memory leaks)
  private readonly maxHistorySize: number;

  /**
   * Create a new CommandManager
   *
   * @param maxHistorySize - Maximum number of commands to keep in history (default: 50)
   */
  constructor(maxHistorySize: number = 50) {
    this.maxHistorySize = maxHistorySize;
  }

  /**
   * Execute a command
   *
   * @param command - Command to execute
   * @param options - Execution options
   * @returns Command execution result
   */
  public async execute(
    command: Command,
    options: ExecuteOptions = { deduplicate: true, recordHistory: true }
  ): Promise<CommandResult> {
    // Deduplication check
    if (options.deduplicate) {
      const operationKey = this.getOperationKey(command);
      const pendingOperation = this.pendingOperations.get(operationKey);

      if (pendingOperation) {
        console.log(`[CommandManager] Skipping duplicate operation: ${operationKey}`);
        return pendingOperation;
      }
    }

    // Validation
    if (!command.canExecute()) {
      console.warn('[CommandManager] Command cannot be executed:', command.description);
      return {
        success: false,
        error: new Error('Command cannot be executed in current state'),
        affectedEntities: {}
      };
    }

    // Execute command
    console.log(`[CommandManager] Executing: ${command.description}`);

    const operationKey = this.getOperationKey(command);
    const executionPromise = command.execute();

    // Track pending operation
    if (options.deduplicate) {
      this.pendingOperations.set(operationKey, executionPromise);
    }

    try {
      const result = await executionPromise;

      if (result.success) {
        // Add to undo stack
        if (options.recordHistory && command.canUndo()) {
          this.undoStack.push(command);
          this.redoStack = []; // Clear redo stack on new operation

          // Limit history size
          if (this.undoStack.length > this.maxHistorySize) {
            this.undoStack.shift();
          }
        }

        console.log(`[CommandManager] Success: ${command.description}`);
      } else {
        console.error(`[CommandManager] Failed: ${command.description}`, result.error);
      }

      return result;
    } finally {
      // Remove from pending operations
      if (options.deduplicate) {
        this.pendingOperations.delete(operationKey);
      }
    }
  }

  /**
   * Undo last command
   */
  public async undo(): Promise<CommandResult> {
    // Peek at command without removing it yet
    const command = this.undoStack[this.undoStack.length - 1];

    if (!command) {
      return {
        success: false,
        error: new Error('Nothing to undo'),
        affectedEntities: {}
      };
    }

    if (!command.canUndo()) {
      return {
        success: false,
        error: new Error('Command cannot be undone'),
        affectedEntities: {}
      };
    }

    // Now remove from stack
    this.undoStack.pop();

    console.log(`[CommandManager] Undoing: ${command.description}`);

    const result = await command.undo();

    if (result.success) {
      this.redoStack.push(command);
      console.log(`[CommandManager] Undo success: ${command.description}`);
    } else {
      // Restore to undo stack if undo failed
      this.undoStack.push(command);
      console.error(`[CommandManager] Undo failed: ${command.description}`, result.error);
    }

    return result;
  }

  /**
   * Redo last undone command
   */
  public async redo(): Promise<CommandResult> {
    const command = this.redoStack.pop();

    if (!command) {
      return {
        success: false,
        error: new Error('Nothing to redo'),
        affectedEntities: {}
      };
    }

    console.log(`[CommandManager] Redoing: ${command.description}`);

    const result = await command.execute();

    if (result.success) {
      this.undoStack.push(command);
      console.log(`[CommandManager] Redo success: ${command.description}`);
    } else {
      // Restore to redo stack if redo failed
      this.redoStack.push(command);
      console.error(`[CommandManager] Redo failed: ${command.description}`, result.error);
    }

    return result;
  }

  /**
   * Check if undo is available
   */
  public canUndo(): boolean {
    const command = this.undoStack[this.undoStack.length - 1];
    return !!command && command.canUndo();
  }

  /**
   * Check if redo is available
   */
  public canRedo(): boolean {
    const command = this.redoStack[this.redoStack.length - 1];
    return !!command && command.canExecute();
  }

  /**
   * Get undo history
   */
  public getUndoHistory(): readonly Command[] {
    return [...this.undoStack];
  }

  /**
   * Get redo history
   */
  public getRedoHistory(): readonly Command[] {
    return [...this.redoStack];
  }

  /**
   * Clear all history
   */
  public clearHistory(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.pendingOperations.clear();
    console.log('[CommandManager] History cleared');
  }

  /**
   * Get stats about current state
   */
  public getStats(): {
    undoStackSize: number;
    redoStackSize: number;
    pendingOperations: number;
  } {
    return {
      undoStackSize: this.undoStack.length,
      redoStackSize: this.redoStack.length,
      pendingOperations: this.pendingOperations.size,
    };
  }

  /**
   * Generate deduplication key for command
   *
   * Commands with same key are considered duplicates
   * Override this method for custom deduplication logic
   */
  protected getOperationKey(command: Command): string {
    // Default: use command ID (no deduplication)
    // Subclasses can override for specific command types
    return command.id;
  }
}
